import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useLocation, useHistory } from "react-router-dom";
import { MdExpandLess, MdExpandMore } from "react-icons/md";

const nodeKey = (n) => n.href || n.title;

const computeInitialOpen = (nodes, pathname, acc = {}) => {
  nodes?.forEach((n) => {
    const k = nodeKey(n);
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    const matchSelf = n.href && pathname.startsWith(n.href);
    const matchChild =
      hasChildren &&
      n.children.some((c) => c.href && pathname.startsWith(c.href));
    acc[k] = Boolean(hasChildren && (matchSelf || matchChild));
    if (hasChildren) computeInitialOpen(n.children, pathname, acc);
  });
  return acc;
};

const SidebarNav = ({ pages, className, onClose, ...rest }) => {
  const location = useLocation();
  const history = useHistory();

  const initial = useMemo(
    () => computeInitialOpen(pages, location.pathname),
    [pages, location.pathname]
  );
  const [openMap, setOpenMap] = useState(initial);

  useEffect(() => setOpenMap(initial), [initial]);

  const toggle = useCallback((k) => {
    setOpenMap((m) => ({ ...m, [k]: !m[k] }));
  }, []);

  const go = useCallback(
    (href) => {
      if (!href) return;
      try {
        history.push(href); // SPA navigation
      } catch {
        window.location.href = href; // fallback
      }
      onClose?.();
    },
    [history, onClose]
  );

  const Row = ({ n, depth }) => {
    const k = nodeKey(n);
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    const isOpen = !!openMap[k];
    const Icon = n.icon;
    const active = n.href && location.pathname.startsWith(n.href);

    return (
      <Fragment key={k}>
        <div
          className={clsx(
            "flex items-center gap-3 rounded-md px-3 py-2 transition cursor-pointer",
            "hover:bg-gray50",
            // depth > 0 && "pl-6"
            active && "bg-gray50"
          )}
          onClick={() => go(n.href)}
        >
          {Icon && <Icon className="h-5 w-5 text-gray500 shrink-0" />}

          <div className="flex-1 text-left font-manrope text-sm font-semibold text-gray700">
            {n.title}
          </div>

          {hasChildren && (
            <div
              className="text-gray500"
              onClick={(e) => {
                e.stopPropagation();
                toggle(k);
              }}
            >
              {isOpen ? (
                <MdExpandLess className="h-5 w-5" />
              ) : (
                <MdExpandMore className="h-5 w-5" />
              )}
            </div>
          )}
        </div>

        {/* children */}
        {hasChildren && isOpen && (
          <div className="ml-3 mt-1 flex flex-col gap-1">
            {n.children.map((c) => (
              <Row key={nodeKey(c)} n={c} depth={depth + 1} />
            ))}
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div {...rest} className={clsx("flex flex-col gap-1", className)}>
      {pages.map((p) => (
        <Row key={nodeKey(p)} n={p} depth={0} />
      ))}
    </div>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      href: PropTypes.string,
      icon: PropTypes.any,
      children: PropTypes.array,
    })
  ).isRequired,
};

export default SidebarNav;
